import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Utilities } from '@common/utilities';
import { AppEnum } from '@enums/AppEnum';
import { PuzzleGameEnum } from '@enums/PuzzleGameEnum';
import { BrainGameControllerService } from '@services/brain-game-controller.service';
import { LevelingService } from '@services/leveling.service';
import { HomeService } from '@services/home.service';
import { StreaksService } from '@services/streaks.service';
import { ModalController } from '@ionic/angular';
import { GameScoreModalComponent } from './helpers/game-score/game-score.component';
import { Subscription } from 'rxjs';


@Component({
  selector: 'app-leveling-screen',
  templateUrl: './leveling-screen.component.html',
  styleUrls: ['./leveling-screen.component.scss'],
})
export class LevelingScreenComponent extends Utilities implements OnInit, OnChanges, OnDestroy {

  appenums = AppEnum;

  // connected to BASE scores in leveling Service
  @Input() App;

  // connected to GAME scores in leveling Service
  @Input() Game;

  // score for whatever we just finished
  @Input() score;

   // if its a phaser game
   @Input() phaser;

   @Input() context;

  @Output() doneClicked = new EventEmitter();
  @Output() retryClicked = new EventEmitter();
  @Output() extraClicked = new EventEmitter();

  /**
   * Optional override for header mode. If omitted or 'auto', the component will infer:
   * - New High Score (if detected)
   * - Difficulty Increase (if leveling caused a level-up)
   * - Great Work (default)
   */
  @Input() headerMode: 'auto' | 'greatWork' | 'difficultyIncrease' | 'newHighScore' = 'auto';

  headerText: string = 'Great Work!';
  subtext: string =
    'Way to put in extra effort! Click Done to see what’s next or Retry to play again.';

  isNewHighScore = false;
  isDifficultyIncrease = false;

  todayApps: Array<{
    name: string;
    param?: string;
    imageUrl?: string;
    state: 'completed' | 'upNext' | 'todo';
  }> = [];

  private subscription = new Subscription();

  constructor(
    private levelService: LevelingService,
    private bgController: BrainGameControllerService,
    private homeService: HomeService,
    private streaksService: StreaksService,
    private modalCtrl: ModalController
  ) { super(); }

  ngOnInit() {
    this.subscription.add(
      this.homeService.appsSorted.subscribe(() => {
        this.computeTodayApps();
      })
    );
    // best-effort initial compute (in case apps already loaded)
    this.computeTodayApps();

    this.subscription.add(
      this.levelService.summary.subscribe(() => {
        this.isDifficultyIncrease = this.levelService.levelsUp > 0;
        this.applyHeaderMode();
      })
    );

    if(this.score === 0){
      console.error(`Score is 0 on ${this.App}, ${this.context}, ${this.Game}`)
    }

    // high score detection is async; don't block render
    this.computeHighScore();
    this.applyHeaderMode();
    return
  };

  async ngOnChanges(changes: SimpleChanges) {
      if(this.score){
        const test = await this.levelService.calculatePoints(this.App, this.Game, this.score);
      }
      if(this.score === 0){
        console.error(`Score is 0 on ${this.App}, ${this.context}, ${this.Game}`)
      }
      this.computeHighScore();
      this.applyHeaderMode();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  trackByApp(_: number, item: any) {
    return item?.param || item?.name || _;
  }

  getScoreText(): string {
    if (this.score === null || this.score === undefined) return '--';
    if (typeof this.score === 'number') return this.score.toFixed(2);
    return String(this.score);
  }

  private computeTodayApps() {
    const completed = (this.homeService.todaysCompliantActivities || []).filter((a) => !!a);
    const todo = (this.homeService.todaysActivities || []).filter((a) => !!a);

    const upNext = todo.length ? todo[0] : null;
    const combined = [...completed, ...todo];

    // De-dupe by param/name while preserving order.
    const seen = new Set<string>();
    const out: any[] = [];
    combined.forEach((app) => {
      const key = app?.param || app?.name;
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push(app);
    });

    this.todayApps = out.map((app) => {
      const isCompleted = completed.some((c) => (c?.param || c?.name) === (app?.param || app?.name));
      const isUpNext = upNext && (upNext?.param || upNext?.name) === (app?.param || app?.name);
      return {
        name: app?.name,
        param: app?.param,
        imageUrl: app?.imageUrl,
        state: isCompleted ? 'completed' : isUpNext ? 'upNext' : 'todo',
      };
    });
  }

  private async computeHighScore() {
    try {
      if (!this.Game || typeof this.score !== 'number') {
        this.isNewHighScore = false;
        return;
      }

      const streaks: any = await this.streaksService.getStreaks();
      if (!streaks) {
        this.isNewHighScore = false;
        return;
      }

      if (this.App === AppEnum.BrainGame) {
        const recent = streaks?.BrainGame?.Overall?.recent ?? null;
        const top = streaks?.BrainGame?.Overall?.top ?? null;
        this.isNewHighScore = recent === this.score && top === recent && this.score > 0;
      } else if (this.App === AppEnum.Yahootie) {
        const recent = streaks?.Yahootie?.recent ?? null;
        const top = streaks?.Yahootie?.top ?? null;
        this.isNewHighScore = recent === this.score && top === recent && this.score > 0;
      } else {
        this.isNewHighScore = false;
      }
    } catch {
      this.isNewHighScore = false;
    }
  }

  private applyHeaderMode() {
    const mode =
      this.headerMode === 'auto'
        ? this.isNewHighScore
          ? 'newHighScore'
          : this.isDifficultyIncrease
            ? 'difficultyIncrease'
            : 'greatWork'
        : this.headerMode;

    if (mode === 'newHighScore') {
      this.headerText = 'New High Score!';
      this.subtext = 'You just set a new personal best.';
      return;
    }

    if (mode === 'difficultyIncrease') {
      this.headerText = 'Difficulty Increase!';
      this.subtext = 'Nice work — you just leveled up.';
      return;
    }

    this.headerText = 'Great Work!';
    this.subtext =
      'Way to put in extra effort! Click Done to see what’s next or Retry to play again.';
  }

  async seeHelper() {
    const modal = await this.modalCtrl.create({
      component: GameScoreModalComponent,
      componentProps: { game: this.Game },
      cssClass: 'gameScore-modal',
      showBackdrop: true,
    });

    modal.present();
  }

  seeAverages(): void {
    // Placeholder code for the seeAverages() function
    this.extraClicked.emit(null);
  }
  
  seeLeaderboard(): void {
    // Placeholder code for the seeLeaderboard() function
    this.extraClicked.emit(null);
  }
  
  done(): void {
    if(this.phaser){
      this.bgController.closeLeaderboardModal.next(true);
    }
    this.doneClicked.emit(null);
  }
  
  retry(): void {
    this.retryClicked.emit(null);
  }

  backToGame() {
    this.bgController.closeLeaderboardModal.next(true);
    return;
  }
  
  changeSummary() {
    // legacy no-op (kept for API stability)
    return;
  }
  
  

}
